import { NgClass, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, filter, take, tap } from 'rxjs';

import { AuthState } from '../models/auth-state';
import { AuthType } from '../models/auth-type.enum';
import { AuthService } from '../services/auth.service';
import { selectAccessToken, selectRefreshToken, selectReloginUrl, setDsToken } from '../store';
import { AuthDsComponent } from './deposit-sign/auth-ds.component';
import { AuthLoginPasswordComponent } from './login-password/auth-login-password.component';
import { AuthOtpComponent } from './otp/auth-otp.component';

@Component({
  selector: 'auth-lib',
  standalone: true,
  imports: [NgOptimizedImage, AuthOtpComponent, AuthDsComponent, AuthLoginPasswordComponent, TranslatePipe, NgClass],
  templateUrl: './auth.component.html',
  styles: `
    auth-ds,
    auth-otp,
    auth-login-password {
      display: block;
    }

    @keyframes auth-type-enter {
      from {
        transform: translateY(10px);
        opacity: 0;
      }
    }

    .auth-type-enter {
      animation: auth-type-enter 0.5s ease-in-out;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly authStore = inject(Store<AuthState>);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly authType = AuthType;

  type = signal(this.authType.Otp);

  ngOnInit(): void {
    this.watchAuthorizationResult();
  }

  watchAuthorizationResult() {
    // For OTP and LoginPassword - only watch successAuthResult$
    this.authService.successAuthResult$
      .pipe(
        tap(success => {
          if (this.type() === AuthType.Otp || this.type() === AuthType.LoginPassword) {
            if (success) {
              this.handleSuccess();
            } else {
              const authType = this.type() === AuthType.Otp ? 'otp' : 'password';
              console.error(`Failed to login with ${authType}.`);
              this.router.navigate(['/login']).then();
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // For DepositSign - use combineLatest since it needs both observables
    combineLatest([this.authService.dsSignResult$, this.authService.successAuthResult$])
      .pipe(
        tap(([dsReceived, success]) => {
          if (this.type() === AuthType.DepositSign) {
            if (dsReceived) this.authStore.dispatch(setDsToken({ dsToken: this.authService.depositSignToken() }));
            if (!dsReceived) this.authStore.dispatch(setDsToken({ dsToken: null }));
            if (success) {
              this.authStore.dispatch(setDsToken({ dsToken: this.authService.depositSignToken() }));
              this.handleSuccess();
            } else {
              console.error('Failed to login with deposit sign.');
              this.router.navigate(['/login']).then();
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected changeAuthType(type: AuthType) {
    this.type.set(type);
    this.authService.type.set(type);
  }

  private handleSuccess() {
    // Wait for tokens to be set in store before navigating
    return combineLatest([this.authStore.select(selectAccessToken), this.authStore.select(selectRefreshToken), this.authStore.select(selectReloginUrl)])
      .pipe(
        // Wait until both tokens are present
        filter(([accessToken, refreshToken]) => !!accessToken && !!refreshToken),
        take(1)
      )
      .subscribe(([, , urlResult]) => {
        this.authService.startAuthTimer();
        if (urlResult) {
          this.router.navigateByUrl(urlResult).then();
        } else {
          this.router.navigate(['/'], { onSameUrlNavigation: 'reload' }).then();
        }
      });
  }
}
